import { t } from "@lingui/macro";
import React from "react";
import { GestureResponderEvent, Platform, Pressable } from "react-native";

import {
  AGENDA_PIXELS_PER_MINUTE,
  AgendaItemContainer,
  COLUMN_MIN_WIDTH_PX,
} from "@whoosh/design-system/src/apps/PatronApp/screens/multiSlotBooking";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { WhooshThemeColors } from "@whoosh/design-system/src/theme/restyle/theme";

import { RestyleColorString } from "../../../../theme/types";

export type Variant = "Yours" | "AvailableToJoin" | "Busy";

type Player = {
  name: string;
  id: string;
  roundId: string;
} | null;

type BookingCardProps = {
  durationMins: number;
  onPress: (
    e?:
      | GestureResponderEvent
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | undefined
  ) => void;
  isMoveBooking: boolean;
  variant: Variant;
  players: Player[];
};

const getBackgroundColor: (variant: Variant) => RestyleColorString = (
  variant
) => {
  switch (variant) {
    case "Yours":
      return "surface-primary";
    case "AvailableToJoin":
      return "surface-main-primary-subtlest";
    case "Busy":
      return "grayscale-200";
    default:
      return "grayscale-200";
  }
};

export const BookingCard = ({
  durationMins,
  onPress,
  isMoveBooking,
  variant,
  players,
}: BookingCardProps) => {
  const getTitle = () => {
    const titles = {
      Yours: t`Your Booking`,
      Busy: t`Busy`,
      AvailableToJoin: t`Available To Join`,
    };
    return titles[variant];
  };

  const getVariantColor = () => {
    const colors = {
      Yours: "main-primary-700",
      Busy: "grayscale-200",
      AvailableToJoin: "surface-main-primary-subtlest",
    };
    return colors[variant] as WhooshThemeColors;
  };

  const validPlayers = players.filter(
    (p): p is NonNullable<Player> => p !== null
  );

  const calculateOverflow = () => {
    const containerHeight = AGENDA_PIXELS_PER_MINUTE * durationMins;
    const topContentHeight = 16 + 16 + 8;
    const availableHeight = containerHeight - topContentHeight - 16; // subtract 16 for bottom padding
    const heightPerName = 20;
    const maxLines =
      availableHeight / heightPerName > 1
        ? Math.floor(availableHeight / heightPerName)
        : 1;
    const playerCount = validPlayers.length;
    const overflowLines =
      playerCount - maxLines > 0 ? playerCount - maxLines : 0;
    const displayPlayers = validPlayers.slice(0, playerCount - overflowLines);
    return {
      displayPlayers: displayPlayers,
      overflowLines: overflowLines,
    };
  };
  const { displayPlayers, overflowLines } = calculateOverflow();
  const title = getTitle();

  const is15MinutesOrLess = durationMins <= 15;

  return (
    <AgendaItemContainer durationMins={durationMins}>
      <Pressable
        onPress={isMoveBooking ? () => {} : onPress}
        //@ts-ignore
        style={{
          display: "flex",
          flex: 1,
          maxWidth: "100%",
          height: "100%",
          borderWidth: 2,
          borderColor: getVariantColor(),
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "white",
          ...Platform.select({
            web: {
              cursor: isMoveBooking
                ? "auto"
                : variant === "Busy"
                ? "not-allowed"
                : "pointer",
            },
          }),
        }}
        disabled={variant === "Busy"}
      >
        <Box
          paddingHorizontal={4}
          paddingVertical={is15MinutesOrLess ? 2 : 3}
          flex={1}
          bg={getBackgroundColor(variant)}
        >
          <Box
            maxWidth={{
              base: Platform.OS === "web" ? "100%" : COLUMN_MIN_WIDTH_PX,
            }}
          >
            <Text
              color={variant === "Busy" ? "grayscale-800" : "main-primary-700"}
              variant="mainSmallSemiBold"
              mb={2}
              fontWeight="600"
            >
              {title}
            </Text>
            <VStack gap={1}>
              {displayPlayers.map((player, i) => {
                return (
                  <Text
                    key={`${player.roundId}`}
                    color="grayscale-1000"
                    ellipsizeMode="tail"
                    variant="mainTiny"
                    fontWeight="600"
                  >
                    {player.name}
                    {overflowLines && i === displayPlayers.length - 1
                      ? t` +${overflowLines} more`
                      : ""}
                  </Text>
                );
              })}
            </VStack>
          </Box>
        </Box>
      </Pressable>
    </AgendaItemContainer>
  );
};
